var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { observable, action, computed, makeObservable, toJS } from 'mobx';
import FinancingStore from './FinancingStore';
// checkoutOffers (NOT the raw offers export) — this.offerIndex follows checkout's own swapped
// order (financing=0/leasing=1/abo=2/buy=3, see constants/core.ts), same convention CheckoutPage.tsx
// already uses (imports checkoutOffers as offers too). The raw `offers` array is ordered
// financing/leasing/buy/abo instead, so offers[3] resolved to 'abo' — a dead, never-rendered slot
// — instead of 'buy', silently writing handleChangeStoreValue's updates (e.g. the Lieferadresse
// radios' withTransportation) into a field nothing ever reads back for a Kaufen session.
import { checkoutSteps, checkoutSubStep, checkoutOffers as offers, checkoutWarranties, offers as financingOffers } from '@bytebrand/fe-ui-core-autobahn/constants';
import _, { isNil, isNumber } from 'lodash';
import { default as borrowerForm } from '../components/pages/CheckoutPage/CheckoutStepper/Content/PaymentDetails/formConfig';
import orderOverviewForm from '../components/pages/CheckoutPage/CheckoutStepper/Content/OrderOverview/formConfig';
import form, { fields } from '../components/pages/CheckoutPage/CheckoutStepper/Content/ContactInformation/formConfig';
import registerForm from '../components/modals/OfferRequestModals/formConfigs/RegistrationFormConfig';
import ProfileStore from './ProfileStore';
import { THOUSAND } from 'framework/constants/app';
import _merge from 'lodash/merge';
import UserActionsContactAPI from 'services/UserActionsContactAPI';
import { history } from '../AppRouter';
import { transformDataForVehicles } from 'framework/DataTransformers';
import WpAPI from 'services/WpAPI';
import { ShowNotificationError } from 'framework/ErrorLogger';
import SearchFiltersStore from './SearchPage/SearchFiltersStore';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import { getComputedFinancing } from '@bytebrand/fe-financing';
// Anschluss-Garantie now offers a single warranty tier — 'gold' (index 2 of fe-financing's
// checkoutWarranties, the same [silver, bronze, gold] table Order Overview's legacy
// warrantyPackage line already reads) — at one of two durations, priced per vehicle via the
// same getComputedFinancing()/getCheckout() pipeline Rundum-Sorglos-Paket's addServicesCost
// already goes through (financing → monthly annuity, leasing → Santander effective-rate
// annuity, cash → one-time amount). anschlussGarantie stays the UI selection index (0 = +2
// Jahre, 1 = +1 Jahr, 2 = keine); this only replaces the static [15, 9, 0] price lookup.
const ANSCHLUSS_GARANTIE_WARRANTY_PACKAGE = 2; // 'gold' — the only tier Anschluss-Garantie offers
const ANSCHLUSS_GARANTIE_PERIOD_BY_INDEX = [2, 1]; // anschlussGarantie index -> warrantyPeriod (years)
// ADV reload bug: externalState (carId, make, model, …) only ever arrives via history.push/
// replace's `state`, set once in CheckoutPage.componentDidMount. A hard reload on e.g.
// /checkout/order-overview re-mounts with an empty externalState (nothing else repopulates it),
// so the "nothing to show" guard there used to fire every time and bounce to /search, wiping an
// in-progress checkout. sessionStorage (not a cookie/localStorage) mirrors getOrCreateAttemptId
// below: survives refresh/back-forward in the same tab, gone once the tab closes or the visitor
// genuinely leaves checkout (resetCheckoutStore). Reading it back feeds the EXISTING
// isSavedCheckout resume path (CheckoutPage.componentDidMount) — the backend already has the full
// record from updateCheckoutStepData on every prior step, including lastPathname to land back on
// the right step; carId is all that path strictly needs. mainImageId/make/model/subModel are
// carried too only so the header/aside car image+name aren't blank for the one render before
// getCheckoutCar's own `car` object resolves — the same fields OfferRequestButtonWrapper's own
// "continue checkout" link already puts on its URL (?carId=&mainImageId=&model=&subModel=&make=).
// anschlussGarantie rides along too for a different reason: it's a genuinely frontend-only field
// (see its @observable comment below, never sent to/read back from the backend at all, unlike
// warrantyPackage), so without this it has NO source to resume from — componentInitializeData
// used to just default it to 2 ("keine") on every resume, which silently overwrote a real +2/+1
// Jahre selection back to "Keine Anschluß-Garantie" on reload instead of merely being a fallback
// for the genuinely-never-chosen case.
const CHECKOUT_RESUME_STORAGE_KEY = 'checkout_resume_state';
/** The checkout in progress in this tab (carId + display fields), or null if none/unavailable. */
export const getPersistedCheckoutResumeState = () => {
    if (typeof sessionStorage === 'undefined')
        return null;
    try {
        const raw = sessionStorage.getItem(CHECKOUT_RESUME_STORAGE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        return parsed && parsed.carId ? parsed : null;
    }
    catch (e) {
        return null;
    }
};
export const getAnschlussGarantiePriceForPeriod = (warrantyPeriod, financingConfig, offerIndex) => {
    // Every caller passes checkoutStore's own offerIndex, which is CHECKOUT-space (buy=3/abo=2) —
    // getComputedFinancing (fe-financing) expects RAW order (buy=2/abo=3), the same swap
    // checkoutStore.getOfferIndex() applies the other direction. Passing checkout-space straight
    // through made Kaufen (checkout 3) get treated as raw 3 = "abo" internally, which called
    // getAbo/getBuy with neither `abo` nor `buy` present on the object below — both threw reading
    // undefined (config.abo.paybackPeriod / config.buy.transportationCost) on every single render
    // once Anschluss-Garantie was chosen (0/1) together with Kaufen. That repeated render-time
    // exception was the actual cause of checkout losing its place on reload, not a resume-state bug.
    const rawOfferIndex = offerIndex === 2 ? 3 : offerIndex === 3 ? 2 : offerIndex;
    const { checkout } = getComputedFinancing({
        offerIndex: rawOfferIndex,
        financing: financingConfig.financing,
        leasing: financingConfig.leasing,
        buy: financingConfig.buy,
        abo: financingConfig.abo,
        common: financingConfig.common,
        checkout: { warrantyPackage: ANSCHLUSS_GARANTIE_WARRANTY_PACKAGE, warrantyPeriod },
    });
    return (checkout && checkout.warrantyRates && checkout.warrantyRates[ANSCHLUSS_GARANTIE_WARRANTY_PACKAGE]) || 0;
};
export const getAnschlussGarantiePrice = (anschlussGarantie, financingConfig, offerIndex) => {
    if (anschlussGarantie == null || anschlussGarantie === 2)
        return 0; // not chosen yet, or declined
    return getAnschlussGarantiePriceForPeriod(ANSCHLUSS_GARANTIE_PERIOD_BY_INDEX[anschlussGarantie], financingConfig, offerIndex);
};
class CheckoutStore {
    constructor() {
        Object.defineProperty(this, "offerIndex", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "readyToRedirect", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "activeStep", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "paymentDetailsOpen", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "promocodeIsOpen", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "scrollToLieferadresse", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "externalState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "subStep", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "warrantyPeriod", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "warrantyPackage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** Anschluss-Garantie selection (0 = +2 Jahre, 1 = +1 Jahr, 2 = keine). Its price is now real
         *  (getAnschlussGarantiePrice — the existing 'gold'-tier warrantyRates, keyed by warrantyPeriod
         *  instead of warrantyPackage), but this field itself is still frontend-only: unlike
         *  warrantyPackage it is NOT sent in updateCheckoutStepData's requestProps below. */
        Object.defineProperty(this, "anschlussGarantie", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // Lieferadresse's in-checkout alternate address and active delivery row — held here rather than
        // only in AdditionalServicesNext's local state, so they survive that step unmounting on Back.
        Object.defineProperty(this, "deliveryAltAddress", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "deliveryChoice", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'home'
        });
        // True from the moment a saved checkout's resume starts until its replay has landed on the
        // target step — the "wrong step" redirect must not fire in that window, when activeStep is
        // already non-zero but the completed set is still empty.
        Object.defineProperty(this, "isResumingCheckout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isSavedCheckout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "borrowerInformation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "steps", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "basePath", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: '/checkout'
        });
        Object.defineProperty(this, "completed", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "contactInformation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "discountCode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "registrationServicesOptions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "addressField", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "defaultConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "checkoutCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "savedCheckouts", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "showButtonErrorMessage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "prevLocations", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "ourTermsAndConditions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // need for init default values and for reset all checkout values, when user exited the checkout
        Object.defineProperty(this, "init", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.offerIndex = this.getOfferIndex();
                this.readyToRedirect = false;
                this.activeStep = 0;
                this.prevLocations = [];
                this.paymentDetailsOpen = false;
                this.promocodeIsOpen = false;
                this.scrollToLieferadresse = false;
                this.externalState = {};
                this.subStep = false;
                this.steps = checkoutSteps;
                this.warrantyPeriod = 1;
                this.warrantyPackage = null;
                this.anschlussGarantie = null;
                this.isSavedCheckout = false;
                this.showButtonErrorMessage = false;
                this.ourTermsAndConditions = '';
                this.borrowerInformation = { firstName: '', lastName: '', birthday: null, borrower: '', email: '' };
                this.completed = new Set();
                this.discountCode = '';
                this.contactInformation = {
                    email: '', phoneMain: '',
                    firstName: '', lastName: '', birthday: null, street: '', number: '', zip: '', city: '', company: ''
                };
                this.registrationServicesOptions = [true, false, false];
                this.addressField = { street: '', number: '', zip: '', city: '' };
                this.defaultConfig = {
                    isStrikeShown: true,
                    firstRegistration: 0,
                    mileage: 0,
                    currentSalesPrice: 1,
                    highestPrice: 1,
                    comparable: { withAddServices: true, withTransportation: true, annualMileage: 0, firstInstallment: 0, paybackPeriod: 48 },
                    financing: { purchasePrice: 0, hasPaymentProtection: true, financialInstitution: '', rateMode: 'financing' }
                }; // this obj is set to fix backend type errors. Remove after updating backend financingConfig types
            }
        });
        /**
         * ADV-183 follow-up (PM, 2026-09-09) — one stable UUID per (car, tab-session) checkout
         * attempt, sent on every step write so BigQuery can tell distinct attempts apart even
         * though the Mongo document is still upserted by (user, car) (see CheckoutDto.ts).
         * sessionStorage (not a cookie) is deliberate: survives refresh/back-forward within the
         * same tab, as the tracking-hardening doc's checkout_attempt_id spec asks for, but a
         * genuinely new tab/session naturally gets a fresh id without any extra logic.
         */
        Object.defineProperty(this, "getOrCreateAttemptId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId) => {
                if (!carId || typeof sessionStorage === 'undefined')
                    return '';
                const storageKey = `checkout_attempt_${carId}`;
                try {
                    const existing = sessionStorage.getItem(storageKey);
                    if (existing)
                        return existing;
                    const attemptId = uuidv4();
                    sessionStorage.setItem(storageKey, attemptId);
                    return attemptId;
                }
                catch (e) {
                    return ''; // storage unavailable (private mode etc.) — non-fatal, just no attempt id this session
                }
            }
        });
        Object.defineProperty(this, "clearAttemptId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId) => {
                if (!carId || typeof sessionStorage === 'undefined')
                    return;
                try {
                    sessionStorage.removeItem(`checkout_attempt_${carId}`);
                }
                catch (e) { /* non-fatal */ }
            }
        });
        // Reads off this.externalState itself (not the possibly-partial `value` just merged in) so the
        // persisted record keeps accumulating fields (make/model/… arrive via a later setExternalState
        // call than carId does, see CheckoutPage.componentDidMount) instead of being clobbered back to
        // whichever call happened to fire last.
        // Only the fields passed in `overrides` change on a call; everything else keeps its last value
        // from storage. offerIndex is written only by handleChangePayment: FinancingStore and navigation
        // state both hold stale values at mount.
        Object.defineProperty(this, "persistResumeState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (overrides = {}) => {
                const { carId, mainImageId, make, model, subModel } = this.externalState;
                if (!carId || typeof sessionStorage === 'undefined')
                    return;
                try {
                    let stored = {};
                    try {
                        stored = JSON.parse(sessionStorage.getItem(CHECKOUT_RESUME_STORAGE_KEY) || '{}') || {};
                    }
                    catch (e) {
                        stored = {};
                    }
                    sessionStorage.setItem(CHECKOUT_RESUME_STORAGE_KEY, JSON.stringify({
                        carId, mainImageId, make, model, subModel,
                        anschlussGarantie: this.anschlussGarantie ?? stored.anschlussGarantie,
                        offerIndex: overrides.offerIndex ?? stored.offerIndex,
                        lastPathname: overrides.lastPathname ?? stored.lastPathname,
                        deliveryChoice: this.deliveryChoice,
                        deliveryAltAddress: this.deliveryAltAddress,
                    }));
                }
                catch (e) { /* non-fatal */ }
            }
        });
        Object.defineProperty(this, "clearResumeState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (typeof sessionStorage === 'undefined')
                    return;
                try {
                    sessionStorage.removeItem(CHECKOUT_RESUME_STORAGE_KEY);
                }
                catch (e) { /* non-fatal */ }
            }
        });
        Object.defineProperty(this, "resetCheckoutStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                // A genuine "exit checkout" clears the attempt id so a later restart on the same car
                // mints a fresh one, rather than resuming the same attempt id indefinitely. Same for the
                // reload-resume key below — once the visitor actually leaves checkout there is nothing left
                // to resume into.
                if (this.externalState?.carId)
                    this.clearAttemptId(this.externalState.carId);
                this.clearResumeState();
                this.deliveryAltAddress = null;
                this.deliveryChoice = 'home';
                this.init();
            }
        });
        Object.defineProperty(this, "setDeliveryAltAddress", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (addr) => {
                this.deliveryAltAddress = addr;
                this.persistResumeState();
            }
        });
        Object.defineProperty(this, "setDeliveryChoice", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (choice) => {
                this.deliveryChoice = choice;
                this.persistResumeState();
            }
        });
        Object.defineProperty(this, "setResumingCheckout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                this.isResumingCheckout = value;
            }
        });
        Object.defineProperty(this, "setReadyToRedirect", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                this.readyToRedirect = value;
            }
        });
        Object.defineProperty(this, "setActiveStep", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (step) => {
                if (!isNil(step)) {
                    this.activeStep = step;
                }
            }
        });
        Object.defineProperty(this, "componentInitializeData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (registrationServicesOptions, offerIndex, borrowerInformation, paymentDetailsOpen, warrantyPackage, warrantyPeriod, externalState, resumedAnschlussGarantie) => {
                this.registrationServicesOptions = registrationServicesOptions;
                this.offerIndex = offerIndex;
                this.borrowerInformation = borrowerInformation;
                this.paymentDetailsOpen = paymentDetailsOpen;
                this.warrantyPackage = warrantyPackage;
                this.warrantyPeriod = warrantyPeriod;
                this.externalState = externalState;
                // Anschluss-Garantie is frontend-only (see the @observable anschlussGarantie comment below) —
                // getCheckoutCar's saved record has no value to restore. resumedAnschlussGarantie is
                // CheckoutPage's own sessionStorage-persisted value (ICheckoutResumeState — the one place this
                // field DOES survive a reload); use it when there is one. Only a genuinely fresh resume with
                // nothing persisted yet (or storage unavailable) falls back to 2 ("keine"), which prices
                // identically to null (getAnschlussGarantiePrice treats them the same) and also satisfies
                // stepHasError/handleComplete's 'additional-services' gate (anschlussGarantie != null) that
                // CheckoutPage's resume replay loop (handleNext(true) per step until lastPathname) depends on
                // to advance past that step instead of silently no-oping and getting stuck there.
                this.anschlussGarantie = resumedAnschlussGarantie != null ? resumedAnschlussGarantie : (this.anschlussGarantie == null ? 2 : this.anschlussGarantie);
                // Re-sync sessionStorage to the now-resolved value: the setExternalState call(s) CheckoutPage
                // makes earlier in the same resume (before this method runs) already persisted a stale
                // this.anschlussGarantie snapshot (null, since it hadn't been resolved yet) as a side effect.
                // Without this, that stale null would stick permanently — a SECOND reload would no longer have
                // the real captured-before-the-clobber value CheckoutPage used for THIS resume, and would fall
                // straight back to the 2 ("keine") default again.
                this.persistResumeState();
            }
        });
        Object.defineProperty(this, "getOfferIndex", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const { getOfferIndex } = FinancingStore;
                switch (getOfferIndex()) {
                    case 2: return 3;
                    case 3: return 2;
                    default: return getOfferIndex();
                }
            }
        });
        Object.defineProperty(this, "getCheckoutSteps", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (history, location, t) => {
                const pathname = location.pathname;
                const steps = checkoutSteps.map((item, i) => ({ ...item, ...t('checkoutSteps', { returnObjects: true })[i] }));
                const step = steps.findIndex(item => pathname.includes(item.pathname)); // get initial activeStep of stepper
                const activeStep = step > -1 ? step : 0;
                const path = this.subStep ? checkoutSubStep.pathname : steps[activeStep].pathname;
                // ADV-279: replace, not push — the URL already is this step, so a push only added a
                // duplicate history entry and browser Back landed on the same step again.
                history.replace({ state: toJS(this.externalState), pathname: `${this.basePath}/${path}` });
                this.activeStep = activeStep;
                this.steps = steps;
                return this.steps;
            }
        });
        Object.defineProperty(this, "setPaymentDetailsOpen", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                this.paymentDetailsOpen = value;
            }
        });
        Object.defineProperty(this, "setPromocodeIsOpen", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                this.promocodeIsOpen = value;
            }
        });
        Object.defineProperty(this, "setScrollToLieferadresse", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                this.scrollToLieferadresse = value;
            }
        });
        Object.defineProperty(this, "setSavedCheckout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                this.isSavedCheckout = value;
            }
        });
        Object.defineProperty(this, "setBasePath", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                this.basePath = value;
            }
        });
        Object.defineProperty(this, "setExternalState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                const merged = { ...this.externalState, ...value };
                // Live choices (payment type, Lieferadresse) come from the resume record, not navigation state.
                const live = getPersistedCheckoutResumeState();
                if (live && live.carId === merged.carId) {
                    if (live.offerIndex != null)
                        merged.offerIndex = live.offerIndex;
                    this.deliveryChoice = live.deliveryChoice ?? 'home';
                    this.deliveryAltAddress = live.deliveryAltAddress ?? null;
                    if (this.deliveryChoice === 'alt' && this.deliveryAltAddress) {
                        const alt = this.deliveryAltAddress;
                        ProfileStore.setDeliveryCheckoutAddressObj(alt);
                        ProfileStore.setDeliveryAddressesProfile(`${alt.firstName} ${alt.lastName} ${alt.street} ${alt.number}, ${alt.zip} ${alt.city}`);
                    }
                }
                this.externalState = merged;
                if (this.externalState.carId)
                    this.persistResumeState();
            }
        });
        Object.defineProperty(this, "setCompleted", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                if (value) {
                    this.completed = value;
                }
            }
        });
        Object.defineProperty(this, "setOfferIndex", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                this.offerIndex = value;
            }
        });
        Object.defineProperty(this, "totalSteps", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return this.steps.length;
            }
        });
        Object.defineProperty(this, "allStepsCompleted", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (this.completed.has('order-overview') && this.completed.has('payment-details') && this.completed.has('additional-services')) {
                    return true;
                }
                if (this.completed.has('order-overview') && this.steps.length === 4) { // for buying type
                    return true;
                }
                return false;
            }
        });
        Object.defineProperty(this, "isLastStep", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return this.activeStep === this.totalSteps() - 1;
            }
        });
        Object.defineProperty(this, "isStepComplete", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (step) => {
                return this.completed?.has(step);
            }
        });
        Object.defineProperty(this, "defaultRegistrationServices", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (this.getOfferIndex() === 3 && !this.allStepsCompleted() && !this.isSavedCheckout) {
                    this.registrationServicesOptions = [false, false, false];
                }
                else if (!this.allStepsCompleted() && !this.isSavedCheckout) {
                    this.registrationServicesOptions = [true, false, false];
                }
                else if (this.getOfferIndex() !== 3 && this.allStepsCompleted) {
                    this.registrationServicesOptions = [true, this.registrationServicesOptions[1], false];
                }
            }
        });
        Object.defineProperty(this, "handleAccordion", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.paymentDetailsOpen = !this.paymentDetailsOpen;
                const newCompleted = new Set(this.completed);
                if (!this.paymentDetailsOpen) {
                    newCompleted.add(this.steps[2].pathname);
                    borrowerForm.showErrors(false);
                } // to hide the error when the accordion is closed
                this.completed = newCompleted;
            }
        });
        Object.defineProperty(this, "phoneSplit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (phone) => ({
                countryCode: phone.substring(0, 3),
                areaCode: phone.substring(3, 6),
                phoneNumber: phone.substring(6)
            })
        });
        Object.defineProperty(this, "getContactInformation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (profileData = {}) => {
                const { phoneMain, ...filteredAddress } = profileData;
                const phoneSplit = !!phoneMain && this.phoneSplit(phoneMain);
                form.init(fields);
                form.set('default', { ...filteredAddress });
                form.$('phoneMain').set('default', { ...phoneSplit });
                form.each((field) => {
                    if (!field.hasNestedFields) {
                        field.reset(false);
                        field.showErrors(false);
                    }
                });
                this.setCompleted(this.handleComplete(true).completed);
            }
        });
        Object.defineProperty(this, "getParseFormData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (formName, stateKeyObj) => {
                const { setDeliveryCheckoutAddressObj, setDeliveryAddressesProfile } = ProfileStore;
                let mainAddress = Object.keys(formName.get()).reduce((acc, item) => {
                    if (item === 'phoneMain') {
                        const { countryCode, areaCode, phoneNumber } = formName.get()[item].value;
                        return { ...acc, phoneMain: `${countryCode}${areaCode}${phoneNumber}` };
                    }
                    return { ...acc, [item]: formName.get()[item].value };
                }, {}); // eslint-disable-line
                formName.each((field) => {
                    if (!field.hasNestedFields && field.hasError) {
                        if (field.path.startsWith('phoneMain.')) {
                            mainAddress = { ...mainAddress, phoneMain: 'invalid' };
                        }
                        else if (field.path.startsWith('birthday')) {
                            mainAddress = { ...mainAddress, birthday: null };
                        }
                        else {
                            mainAddress = { ...mainAddress, [field.path]: '' };
                        }
                    }
                });
                // the contact address is the delivery address only while the home row is active
                if (stateKeyObj === 'contactInformation' && this.deliveryChoice === 'home') {
                    const setCurrentDeliveryAddress = `${mainAddress.firstName} ${mainAddress.lastName} ${mainAddress.street} ${mainAddress.number}, ${mainAddress.zip} ${mainAddress.city}`;
                    const setCurrentDeliveryAddressObj = {
                        firstName: mainAddress.firstName,
                        lastName: mainAddress.lastName,
                        city: mainAddress.city,
                        number: mainAddress.number,
                        street: mainAddress.street,
                        zip: mainAddress.zip
                    };
                    setDeliveryCheckoutAddressObj(setCurrentDeliveryAddressObj);
                    setDeliveryAddressesProfile(setCurrentDeliveryAddress);
                }
                formName.submit();
                return { [stateKeyObj]: { ...this[stateKeyObj], ...mainAddress } };
            }
        });
        Object.defineProperty(this, "handleComplete", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (complete) => {
                let formData = {};
                const newComplete = () => {
                    if (complete)
                        return true;
                    switch (this.steps[this.activeStep].pathname) {
                        case 'contact-information':
                            formData = this.getParseFormData(form, 'contactInformation');
                            if (form.hasError) {
                                return !form.hasError;
                            }
                            return true;
                        case 'payment-details':
                            if (this.paymentDetailsOpen) {
                                formData = this.getParseFormData(borrowerForm, 'borrowerInformation');
                                if (borrowerForm.hasError) {
                                    return false;
                                }
                                this.borrowerInformation = formData.borrowerInformation;
                                // }
                            }
                            return true;
                        case 'additional-services':
                            return this.anschlussGarantie != null; // case for step 4 - warranties & services
                        case 'order-overview':
                            if (orderOverviewForm.hasError) {
                                return false;
                            }
                            ;
                        default: return true;
                    }
                };
                const newCompleted = new Set(this.completed);
                newComplete() ? newCompleted.add(this.steps[this.activeStep].pathname) : newCompleted.delete(this.steps[this.activeStep].pathname);
                return { completed: newCompleted, ...formData };
            }
        });
        Object.defineProperty(this, "redirectStepperUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (replace) => {
                const pathname = this.subStep ? checkoutSubStep.pathname : this.steps[this.activeStep].pathname;
                if (!this.subStep)
                    this.persistResumeState({ lastPathname: pathname });
                const location = { state: toJS(this.externalState), pathname: `${this.basePath}/${pathname}` };
                // ADV-279: after a browser Back the URL has already moved — replace so every Back doesn't
                // push a new entry (which dropped the forward history and re-trapped the next Back).
                if (replace)
                    history.replace(location);
                else
                    history.push(location);
            }
        });
        Object.defineProperty(this, "onChangeRegistrationServices", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (e) => {
                const key = +e.target.value;
                this.registrationServicesOptions.forEach((option, index) => {
                    switch (key) {
                        case 2:
                            this.registrationServicesOptions[index] = index === 2;
                            break;
                        case index:
                            this.registrationServicesOptions[index] = !option;
                            break;
                        default:
                            this.registrationServicesOptions[2] = false;
                            if (!this.registrationServicesOptions[0])
                                this.registrationServicesOptions[1] = false;
                    }
                });
                this.completed = this.handleComplete().completed;
            }
        });
        Object.defineProperty(this, "handleChangeStoreValue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (e, value, key) => {
                const { carId } = this.externalState;
                const { getUserConfig, setUserConfig } = FinancingStore;
                const financingConfig = getUserConfig(carId) || {};
                const offerName = offers[this.offerIndex].name;
                if (key === 'warrantyPeriod' && isNumber(value)) {
                    this.warrantyPeriod = value;
                }
                if (key === 'warrantyPackage' && isNumber(value)) {
                    this.warrantyPackage = value;
                }
                if (key === 'anschlussGarantie' && isNumber(value)) {
                    this.anschlussGarantie = value;
                    // Not folded into the setExternalState-driven persistResumeState above — this field isn't
                    // part of externalState and setExternalState isn't called again on a plain picker click, so
                    // without this a reload right after choosing (before the next step transition) would still
                    // lose the pick. See the ICheckoutResumeState comment for why this needs resuming at all.
                    this.persistResumeState();
                }
                this.completed = this.handleComplete(this.steps[this.activeStep].pathname === 'additional-services'
                    && value != null
                    && this.registrationServicesOptions.includes(true)).completed;
                setUserConfig(carId, {
                    ...financingConfig,
                    checkout: { ...financingConfig.checkout, [key]: value },
                    comparable: {
                        ...financingConfig.comparable,
                        [key]: value,
                        isUpdated: Math.round(Date.now() / THOUSAND)
                    },
                    [offerName]: { ...financingConfig[offerName], [key]: value }
                });
            }
        });
        Object.defineProperty(this, "handleChangePayment", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (e) => {
                const { getUserConfig, setUserConfig } = FinancingStore;
                const offerIndex = +e.target.value;
                // TODO: add abo index
                const index = offerIndex === 3 ? 2 : offerIndex;
                FinancingStore.setOfferIndex(index);
                const financingConfig = getUserConfig(this.externalState.carId);
                const completed = this.handleComplete().completed;
                if (offerIndex !== 3 && !completed.has('payment-details')) { // set step 4 not complete if payment-details is not complete
                    completed.delete('additional-services');
                }
                else if (offerIndex === 3 && completed.has('order-overview')) {
                    completed.add('additional-services');
                }
                this.completed = completed;
                this.offerIndex = offerIndex;
                setUserConfig(toJS(this.externalState.carId), { ...financingConfig, offerIndex });
                // history state is built from externalState, so it has to carry the live mode too
                this.externalState = { ...this.externalState, offerIndex: index };
                // Mirror into the reload-resume sessionStorage snapshot right away — same reasoning as the
                // anschlussGarantie persistResumeState call in handleChangeStoreValue above.
                this.persistResumeState({ offerIndex: index });
            }
        });
        Object.defineProperty(this, "stepHasError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (step, subStep) => {
                const pathName = this.steps[step].pathname;
                const borrowerEmailValue = borrowerForm.$('email').value.toLowerCase();
                const contactInformationEmail = this.contactInformation.email.toLowerCase();
                switch (pathName) {
                    case 'contact-information':
                        if (form.hasError) {
                            return true;
                        }
                        else {
                            return false;
                        }
                    case 'payment-details':
                        if (this.paymentDetailsOpen &&
                            borrowerForm.hasError &&
                            (borrowerEmailValue !== contactInformationEmail &&
                                !this.isStepComplete(pathName)) ||
                            (this.paymentDetailsOpen && borrowerEmailValue === contactInformationEmail)) {
                            return true;
                        }
                        else {
                            return false;
                        }
                    case 'additional-services':
                        if (!subStep && this.anschlussGarantie === null) { // case for step 4 - warranties & services
                            return true;
                        }
                        else {
                            return false;
                        }
                    case 'order-overview': {
                        if (orderOverviewForm.hasError && this.promocodeIsOpen) {
                            return true;
                        }
                        const verjField = orderOverviewForm.$('verjaehrungsfrist');
                        // Hidden + not required for a new car (OrderOverviewNext clears its 'rules' when so) —
                        // skip the checked-check then, or checkout could never complete for a new-car purchase.
                        const verjRequired = !!verjField.rules;
                        if ((verjRequired && !verjField.checked) || !orderOverviewForm.$('aktualisierung').checked) {
                            return true;
                        }
                        return false;
                    }
                    default: return false;
                }
            }
        });
        // Reveals the "7. Ergänzende Regelungen" checkboxes' red-border error state (mobx-react-form
        // gates field.error behind a showError flag that a bare validate() leaves false — see
        // Validator#validate's `showErrors` default). stepHasError is a pure check called on every step
        // transition (setStep, below) as well as on an actual buy attempt, so this reveal can't live
        // inside it — that made the checkboxes render red on page load/refresh, before the user ever
        // clicked "Jetzt Kaufen". Call this explicitly only where a blocked BUY attempt is confirmed.
        Object.defineProperty(this, "revealOrderOverviewTermsErrors", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                orderOverviewForm.validate({ showErrors: true });
            }
        });
        Object.defineProperty(this, "popPrevLocations", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.prevLocations.pop();
            }
        });
        /**
         * The car's config as the checkout saves it, with the payment type the checkout shows
         * (this.offerIndex). FinancingStore's config keeps the offerName of its last calculation: Kaufen
         * picked on the VDP doesn't rewrite it, so a checkout started there was saved (and listed on
         * /account/my-orders) as Finanzierung.
         */
        Object.defineProperty(this, "configToSave", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId) => {
                const config = { ...FinancingStore.getUserConfig(carId), purchasePrice: 0 }; // remove purchasePrice after backend will remove at checkout PUT request
                const offer = offers[this.offerIndex];
                return offer ? { ...config, offerName: offer.name, offerIndex: FinancingStore.getOfferIndex() } : config;
            }
        });
        Object.defineProperty(this, "setStep", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ({ step, subStep = false, completed, rest, isResuming, isBackButton }) => {
                this.showButtonErrorMessage = false;
                // A "Zurück" click re-validates the step you're LEAVING (stepHasError), same as advancing —
                // so an order-overview visitor who hasn't ticked both "7. Ergänzende Regelungen" checkboxes
                // (stepHasError's own 'order-overview' case) gets silently stuck: this branch returns early,
                // showButtonErrorMessage flips true, but nothing on that page reads/displays it, so the click
                // just does nothing. Going backward should never require the page you're leaving to be valid
                // — only forward/submit needs that — so skip the check when isBackButton.
                if (!isBackButton && this.stepHasError(this.activeStep, this.subStep)) {
                    this.showButtonErrorMessage = true;
                    const stepUrl = `${this.basePath}/${this.steps[this.activeStep].pathname}`;
                    if (isBackButton)
                        history.replace(stepUrl); // ADV-279: don't grow the history on Back
                    else
                        history.push(stepUrl);
                    return;
                }
                if (!isBackButton)
                    this.prevLocations = [...this.prevLocations, this.steps[this.activeStep].pathname];
                const { carId = '' } = this.externalState;
                const deliveryInformation = _.cloneDeep(ProfileStore.selectedDeliveryCheckoutAddressObj);
                const financingConfig = this.configToSave(carId);
                financingConfig.checkout.licensePlateFeeFinalInstallment = 0; // remove line after backend will remove at checkout PUT request
                financingConfig.checkout.admissionServiceFinalInstallment = 0; // remove line after backend will remove at checkout PUT request
                const isSuccess = step != null;
                const pathname = this.steps[this.activeStep].pathname;
                const { checkout: { warrantyRates } } = financingConfig;
                const financingConfigWithTypes = _merge(this.defaultConfig, financingConfig);
                const warrantyPackageName = this.warrantyPackage == null ? 'invalid' : checkoutWarranties[this.warrantyPackage].name.toString();
                const warrantyPackagePrice = this.warrantyPackage == null ? 'invalid' : (warrantyRates[this.warrantyPackage] || 0).toString();
                const newCompleted = new Set(completed);
                if (!this.isLastStep() && !!step)
                    newCompleted.add(this.steps[step].pathname); // set new step to always complete, so we does not see initial errors
                const lastPathname = Array.from(newCompleted).pop();
                const requestProps = {
                    pathname, contactInformation: toJS(this.contactInformation), financingConfig: financingConfigWithTypes, deliveryInformation, lastPathname,
                    warrantyPeriod: this.warrantyPeriod,
                    borrowerInformation: toJS(this.borrowerInformation),
                    isSuccess: false,
                    checkoutAttemptId: this.getOrCreateAttemptId(carId),
                    ...rest,
                    warrantyPackageName,
                    warrantyPackage: this.warrantyPackage,
                    warrantyPackagePrice: this.warrantyPackage !== 3 ? warrantyPackagePrice : '0',
                    isAdmissionService: this.registrationServicesOptions.includes(true) ? this.registrationServicesOptions[0] : 'invalid',
                    isLicensePlateFee: this.registrationServicesOptions.includes(true) ? this.registrationServicesOptions[1] : 'invalid',
                    isAllowYourself0: this.registrationServicesOptions.includes(true) ? this.registrationServicesOptions[2] : 'invalid'
                };
                this.completed = completed;
                if (rest?.contactInformation) {
                    this.contactInformation = rest.contactInformation;
                }
                if (!isResuming) {
                    UserActionsContactAPI.updateCheckoutStepData(requestProps);
                }
                if (!isSuccess)
                    return;
                this.completed = newCompleted;
                this.activeStep = step;
                this.subStep = subStep;
                // the resume replay rewrites the current entry instead of pushing one per restored step
                this.redirectStepperUrl(isBackButton || this.isResumingCheckout);
            }
        });
        Object.defineProperty(this, "handleStep", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (newActiveStep, isBackButton) => () => {
                return (this.completed.has(this.steps[newActiveStep].pathname) || newActiveStep === this.activeStep + 1) // set new step if step is complete or we want to go to next step
                    && this.setStep({ step: newActiveStep, subStep: false, completed: this.completed, isResuming: true, isBackButton });
            }
        });
        Object.defineProperty(this, "handleNext", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (isResuming) => {
                const { completed, ...rest } = this.handleComplete();
                let newStep;
                let newSubStep = false;
                if (!completed.has(this.steps[this.activeStep].pathname)) { // not complete
                }
                else if (this.allStepsCompleted() && !(this.activeStep === this.steps.length - 2 && !this.subStep)) { // set last step
                    newStep = this.steps.length - 1;
                }
                else { // regular step change
                    // if (!!subStep) this.handleAccordionDelivery(false); // close different delivery form
                    const newActiveStep = this.isLastStep() && !this.allStepsCompleted()
                        ? this.steps.findIndex((step, i) => !completed.has(i))
                        : this.activeStep + 1;
                    newStep = newActiveStep;
                }
                this.setStep({ step: newStep, subStep: newSubStep, completed, rest, isResuming });
            }
        });
        Object.defineProperty(this, "setShowButtonErrorMessage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                this.showButtonErrorMessage = value;
            }
        });
        Object.defineProperty(this, "buyNowInvalid", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (step) => {
                this.showButtonErrorMessage = false;
                if (this.stepHasError(this.activeStep, this.subStep)) {
                    this.revealOrderOverviewTermsErrors();
                    this.showButtonErrorMessage = true;
                    return;
                }
                const { carId = '' } = this.externalState;
                const financingConfig = this.configToSave(carId);
                const { completed, ...rest } = this.handleComplete(); // pass form completed, form values.
                const isSuccess = step != null;
                const pathname = this.steps[this.activeStep].pathname;
                const discountCode = this.promocodeIsOpen ? orderOverviewForm.$('discountCode').value : '';
                const offerName = offers[this.offerIndex].name;
                const { [offerName]: { withTransportation, withAddServices }, checkout: { admissionService, licensePlateFee, warrantyRates } } = financingConfig;
                const warrantyPackageName = this.warrantyPackage == null ? 'invalid' : checkoutWarranties[this.warrantyPackage].name;
                const warrantyPackagePrice = this.warrantyPackage == null ? 'invalid' : (warrantyRates[this.warrantyPackage] || 0).toString();
                const warrantyRate = (this.warrantyPackage == null || this.warrantyPackage === 3) ? 0 : warrantyRates[this.warrantyPackage];
                const totalCost = warrantyRate + +this.registrationServicesOptions[0] * admissionService + +this.registrationServicesOptions[1] * licensePlateFee;
                const deliveryInformation = _.cloneDeep(ProfileStore.selectedDeliveryCheckoutAddressObj);
                const financingConfigUpdated = {
                    offerName: financingConfig.offerName,
                    financing: financingConfig.financing,
                    checkout: {
                        ...financingConfig.checkout,
                        discountCode,
                        contactInformation: this.contactInformation,
                        borrowerInformation: this.borrowerInformation,
                        deliveryInformation,
                        warrantyPackage: this.warrantyPackage,
                        warrantyPackageName,
                        warrantyPackagePrice,
                        totalCost,
                        isAdmissionService: this.registrationServicesOptions[0],
                        isLicensePlateFee: this.registrationServicesOptions[1],
                        isAllowYourself0: this.registrationServicesOptions[2]
                    },
                    leasing: {
                        ...financingConfig.leasing,
                        withTransportation,
                        withAddServices
                    },
                    buy: { ...financingConfig.buy, withTransportation }
                };
                const financingConfigWithTypes = _merge(this.defaultConfig, financingConfig, financingConfigUpdated);
                const newCompleted = new Set(completed);
                if (!this.isLastStep())
                    newCompleted.add(this.steps[step].pathname); // set new step to always complete, so we does not see initial errors
                const lastPathname = Array.from(newCompleted).pop();
                const marketingCookies = Cookies.get('marketing') || null;
                const marketing = { ...JSON.parse(marketingCookies), _ga: Cookies.get('_ga') || null };
                const requestProps = {
                    marketing, isSuccess, pathname, contactInformation: toJS(this.contactInformation), financingConfig: financingConfigWithTypes, borrowerInformation: toJS(this.borrowerInformation), warrantyPeriod: this.warrantyPeriod, lastPathname,
                    checkoutAttemptId: this.getOrCreateAttemptId(carId),
                    ...rest,
                    deliveryInformation: _.cloneDeep(ProfileStore.selectedDeliveryCheckoutAddressObj),
                    warrantyPackage: this.warrantyPackage, warrantyPackageName, warrantyPackagePrice,
                    isAdmissionService: this.registrationServicesOptions.includes(true) ? this.registrationServicesOptions[0] : 'invalid',
                    isLicensePlateFee: this.registrationServicesOptions.includes(true) ? this.registrationServicesOptions[1] : 'invalid',
                    isAllowYourself0: this.registrationServicesOptions.includes(true) ? this.registrationServicesOptions[2] : 'invalid',
                    carId,
                    offerIndex: financingConfigWithTypes.offerIndex,
                    lastSearchString: toJS(SearchFiltersStore.lastSearchString),
                    callBackRequest: registerForm.$('timestamp').value
                };
                if (rest?.contactInformation) {
                    this.contactInformation = rest.contactInformation;
                }
                else if (rest?.borrowerInformation) {
                    this.borrowerInformation = rest.borrowerInformation;
                }
                this.completed = completed;
                UserActionsContactAPI.updateCheckoutStepData(requestProps)
                    .then(() => {
                    if (!isSuccess)
                        return;
                    // the order is saved (the server drops its checkout): no "Bestellung abschließen" for this
                    // car any more, and the Konto menu count is asked again
                    this.forgetCheckout(carId);
                    ProfileStore.getRequestedCarsCount();
                });
            }
        });
        /** The checkouts in progress as saved (each with its car), and their cars as the dashboard lists them. */
        Object.defineProperty(this, "setSavedCheckouts", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (records) => {
                this.savedCheckouts = records;
                this.checkoutCars = observable(records.map((item) => transformDataForVehicles(item.car)));
            }
        });
        Object.defineProperty(this, "forgetCheckout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId) => {
                this.setSavedCheckouts(this.savedCheckouts.filter((item) => (item.car && item.car._id) !== carId));
            }
        });
        /**
         * "Angebot anfragen" starts the car's checkout: it is saved at the first step, as "Weiter" saves
         * a step (the VDP's calculation, the contact data from the profile), so the dashboard offers to
         * finish it and lists it as an order, as any checkout in progress. A checkout already in
         * progress, or done (its contract, with an order id), is left as it is.
         */
        Object.defineProperty(this, "saveRequestedCheckout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId, config, offerIndex) => {
                return UserActionsContactAPI.getCheckoutCar(carId)
                    .then((saved) => {
                    if (saved && (saved.pathname || saved.orderId))
                        return;
                    const { email = '', birthday = null, privateAddress = {} } = (toJS(ProfileStore.userProfile) || {});
                    const contactInformation = {
                        email, phoneMain: '', firstName: '', lastName: '', birthday, street: '', number: '', zip: '', city: '', company: '',
                        ..._.pick(privateAddress, ['firstName', 'lastName', 'phoneMain', 'street', 'number', 'zip', 'city', 'company']),
                    };
                    const offer = financingOffers[offerIndex];
                    const financingConfig = _merge({}, this.defaultConfig, toJS(config), {
                        carId, offerIndex, ...(offer ? { offerName: offer.name } : {}), purchasePrice: 0,
                    });
                    return UserActionsContactAPI.updateCheckoutStepData({
                        carId, isSuccess: false, pathname: checkoutSteps[0].pathname, contactInformation, financingConfig,
                        checkoutAttemptId: this.getOrCreateAttemptId(carId),
                    }).then(() => undefined);
                })
                    .catch(() => undefined);
            }
        });
        Object.defineProperty(this, "getCheckoutTermsAndCond", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return WpAPI.getTermsAndCond()
                    .then((response) => {
                    const { content: { rendered } } = response;
                    this.ourTermsAndConditions = rendered;
                }).
                    catch(() => {
                    this.ourTermsAndConditions = '';
                    return ShowNotificationError();
                });
            }
        });
        makeObservable(this);
        this.init();
    }
    updatePrevLocations(location) {
        this.prevLocations = [...this.prevLocations, location];
    }
    get isPaymentDetailsOpen() {
        return this.paymentDetailsOpen;
    }
    // Always fresh: a checkout finished a moment ago ("Jetzt kaufen") must not be offered again.
    getCheckoutCarList() {
        return UserActionsContactAPI.getCheckoutCarList()
            .then((results) => this.setSavedCheckouts(results.filter((item) => item.car && item.pathname)))
            .catch(() => {
            this.setSavedCheckouts([]);
            return [];
        });
    }
}
__decorate([
    observable,
    __metadata("design:type", Number)
], CheckoutStore.prototype, "offerIndex", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], CheckoutStore.prototype, "readyToRedirect", void 0);
__decorate([
    observable,
    __metadata("design:type", Number)
], CheckoutStore.prototype, "activeStep", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], CheckoutStore.prototype, "paymentDetailsOpen", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], CheckoutStore.prototype, "promocodeIsOpen", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], CheckoutStore.prototype, "scrollToLieferadresse", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "externalState", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], CheckoutStore.prototype, "subStep", void 0);
__decorate([
    observable,
    __metadata("design:type", Number)
], CheckoutStore.prototype, "warrantyPeriod", void 0);
__decorate([
    observable,
    __metadata("design:type", Number)
], CheckoutStore.prototype, "warrantyPackage", void 0);
__decorate([
    observable,
    __metadata("design:type", Number)
], CheckoutStore.prototype, "anschlussGarantie", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "deliveryAltAddress", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], CheckoutStore.prototype, "deliveryChoice", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], CheckoutStore.prototype, "isResumingCheckout", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], CheckoutStore.prototype, "isSavedCheckout", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "borrowerInformation", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], CheckoutStore.prototype, "steps", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], CheckoutStore.prototype, "basePath", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "completed", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "contactInformation", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], CheckoutStore.prototype, "discountCode", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], CheckoutStore.prototype, "registrationServicesOptions", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "addressField", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "checkoutCars", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], CheckoutStore.prototype, "savedCheckouts", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], CheckoutStore.prototype, "showButtonErrorMessage", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], CheckoutStore.prototype, "prevLocations", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], CheckoutStore.prototype, "ourTermsAndConditions", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "init", void 0);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], CheckoutStore.prototype, "updatePrevLocations", null);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "resetCheckoutStore", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "setDeliveryAltAddress", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "setDeliveryChoice", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "setResumingCheckout", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "setReadyToRedirect", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "setActiveStep", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "componentInitializeData", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "getOfferIndex", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "getCheckoutSteps", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "setPaymentDetailsOpen", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "setPromocodeIsOpen", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "setScrollToLieferadresse", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], CheckoutStore.prototype, "isPaymentDetailsOpen", null);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "setSavedCheckout", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "setBasePath", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "setExternalState", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "setCompleted", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "setOfferIndex", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "totalSteps", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "allStepsCompleted", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "isLastStep", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "isStepComplete", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "defaultRegistrationServices", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "handleAccordion", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "getContactInformation", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "handleComplete", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "redirectStepperUrl", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "onChangeRegistrationServices", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "handleChangeStoreValue", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "handleChangePayment", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "stepHasError", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "revealOrderOverviewTermsErrors", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "popPrevLocations", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "setStep", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "handleStep", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "handleNext", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "setShowButtonErrorMessage", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "buyNowInvalid", void 0);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], CheckoutStore.prototype, "getCheckoutCarList", null);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "setSavedCheckouts", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "forgetCheckout", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "saveRequestedCheckout", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], CheckoutStore.prototype, "getCheckoutTermsAndCond", void 0);
export default new CheckoutStore();
